import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type TeamTaskSpeed = '1x' | '1.5x';
export type TeamFlowTaskSpeed = TeamTaskSpeed;
export type TeamFlowQueueMode = TeamTaskSpeed;

const DEFAULT_TASK_SPEED: TeamTaskSpeed = '1x';
const TASK_SPEED_STORAGE_KEY = 'roboneo-team-task-speed';

interface TaskSpeedState {
  /** 任务速率：直接使用接口 task_speed 字段值。 */
  taskSpeed: TeamTaskSpeed;
}

/**
 * 校验本地缓存的任务速率，避免历史脏数据透传到接口。
 *
 * @param {unknown} value - 待校验的缓存值
 * @returns {value is TeamTaskSpeed} 是否为接口支持的任务速率
 */
function isTeamTaskSpeed(value: unknown): value is TeamTaskSpeed {
  return value === '1x' || value === '1.5x';
}

/**
 * 从 zustand persist 的缓存结构中读取任务速率，异常值统一回退默认标准速率。
 *
 * @param {unknown} persistedState - persist 读取到的缓存状态
 * @returns {TeamTaskSpeed} 可安全透传给接口的任务速率
 */
function getPersistedTaskSpeed(persistedState: unknown): TeamTaskSpeed {
  if (!persistedState || typeof persistedState !== 'object') return DEFAULT_TASK_SPEED;
  const { taskSpeed } = persistedState as Partial<TaskSpeedState>;
  return isTeamTaskSpeed(taskSpeed) ? taskSpeed : DEFAULT_TASK_SPEED;
}

const useTaskSpeedStore = create<TaskSpeedState>()(persist(
  () => ({
    taskSpeed: DEFAULT_TASK_SPEED,
  }),
  {
    name: TASK_SPEED_STORAGE_KEY,
    version: 1,
    partialize: (state) => ({ taskSpeed: state.taskSpeed }),
    merge: (persistedState, currentState) => ({
      ...currentState,
      taskSpeed: getPersistedTaskSpeed(persistedState),
    }),
  },
));

export default useTaskSpeedStore;

/**
 * 设置任务速率，后续计价和执行接口会透传为 task_speed。
 *
 * @export
 * @param {TeamTaskSpeed} taskSpeed - 接口 task_speed 字段值
 */
export function setTaskSpeed(taskSpeed: TeamTaskSpeed) {
  useTaskSpeedStore.setState(() => ({ taskSpeed }));
}

/**
 * 读取当前任务速率，供非 React 调用链创建请求体时使用。
 */
export function getTaskSpeed(): TeamTaskSpeed {
  return useTaskSpeedStore.getState().taskSpeed;
}
